import { useState, useEffect, useMemo, useCallback } from "react";
import { useUserModule } from "../../../contexts/UserModuleContext";
import { useUserContext } from "../../../contexts/UserContext";
import { useEquipmentContext } from "../../../contexts/EquipmentContext";
import { getSkillLevelColor } from "../../../utils/skillLevelColors";
import { toLocalCalendarDateString } from "../../../utils/dateInputUtils";
import {
    collectSkillEvalHistoryFromChangeLog,
    collectTaskEvaluationHistory,
    formatEvaluationDateDisplay,
    isSkillEvaluated,
    toEvaluationCalendarDate,
} from "../../../utils/userModuleUtils";
import { DateField } from "../../ui";
import type { SkillAssignment, AssignSkillInput } from "../../../types/userModuleTypes";
import "../../../css/UserModuleModals.css";

interface EditTaskModalProps {
    visible: boolean;
    taskId: string;
    taskName: string;
    categoryName: string;
    classificationName: string;
    userId: string;
    existingAssignment?: SkillAssignment | null;
    onClose: () => void;
    onSave: (input: AssignSkillInput) => void;
}

/**
 * EditTaskModal — Grade proficiency for a specific task assignment.
 * Shows all machine #s for this user+task as selectable pills.
 * Evaluator picks which machines to grade, then sets skill level + eval date.
 * The grade is applied to all selected assignment records.
 */
export const EditTaskModal = ({
    visible,
    taskId,
    taskName,
    categoryName,
    classificationName,
    userId,
    existingAssignment: _existingAssignment,
    onClose,
    onSave,
}: EditTaskModalProps) => {
    const { skillLevels, categories, tasks, subCategories, skillAssignments, changeLogEntries, fetchChangeLog } =
        useUserModule();
    const { companyUser, companyOwner } = useUserContext();
    const { companyUnits } = useEquipmentContext();

    const adminName = companyUser?.name ?? companyOwner?.name ?? "Admin";
    const companyId = (companyUser?.companyId as string) ?? companyOwner?.companyId ?? "";

    // Default skill level options if none defined by company
    const defaultLevels = ["Good", "Average", "Developing", "Not Evaluated"];
    const levelOptions = skillLevels.length > 0
        ? [...skillLevels].sort((a, b) => {
            if (a.name === "Not Evaluated") return 1;
            if (b.name === "Not Evaluated") return -1;
            return a.sortOrder - b.sortOrder;
        }).map((sl) => sl.name)
        : defaultLevels;

    // Find ALL assignments for this user + task
    const taskAssignments = useMemo(() => {
        return skillAssignments.filter((a) => a.taskId === taskId && a.userId === userId);
    }, [skillAssignments, taskId, userId]);

    // Resolve machine # names for each assignment
    const resolveMachineName = useCallback(
        (subId: string | null | undefined): string | null => {
            if (!subId) return null;
            const task = tasks.find((t) => t.id === taskId);
            const category = task ? categories.find((c) => c.id === task.categoryId) : null;
            if (category?.equipmentCategoryId) {
                const unit = companyUnits.find((u) => u.id === subId);
                if (unit) return unit.name || subId;
            }
            const subCat = subCategories.find((sc) => sc.id === subId);
            return subCat?.name || subId;
        },
        [taskId, tasks, categories, companyUnits, subCategories],
    );

    const machineOptions = useMemo(() => {
        return taskAssignments
            .filter((a) => a.subCategoryId)
            .map((a) => {
                const subId = a.subCategoryId!;
                return {
                    id: a.id,
                    subCategoryId: subId,
                    name: resolveMachineName(subId) || subId,
                    level: a.skillLevel || "Not Evaluated",
                };
            });
    }, [taskAssignments, resolveMachineName]);

    const evaluationHistory = useMemo(() => {
        const fromAssignments = collectTaskEvaluationHistory(taskAssignments, resolveMachineName);
        const fromChangeLog = collectSkillEvalHistoryFromChangeLog(
            changeLogEntries.filter((entry) => entry.userId === userId),
            taskName,
        );
        // Prefer assignment-backed history when present; always merge change-log extras
        const seen = new Set(
            fromAssignments.map(
                (entry) =>
                    `${entry.evaluationDate}|${entry.skillLevel}|${entry.evaluatedByName || ''}|${entry.subCategoryId || entry.machineName || ''}`,
            ),
        );
        const merged = [...fromAssignments];
        for (const entry of fromChangeLog) {
            const key = `${entry.evaluationDate}|${entry.skillLevel}|${entry.evaluatedByName || ''}|${entry.subCategoryId || entry.machineName || ''}`;
            if (seen.has(key)) continue;
            seen.add(key);
            merged.push(entry);
        }
        return merged.sort((a, b) =>
            (b.evaluationDate || '').localeCompare(a.evaluationDate || ''),
        );
    }, [taskAssignments, changeLogEntries, userId, taskName, resolveMachineName]);

    // Form state
    const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<Set<string>>(new Set());
    const [selectedLevel, setSelectedLevel] = useState<string>("");
    const [evaluatedBy, setEvaluatedBy] = useState<string>(adminName);
    const [evaluationDate, setEvaluationDate] = useState<string>(() => toLocalCalendarDateString());

    const requiresMachineSelection = machineOptions.length > 0;

    // Reset form when modal opens
    useEffect(() => {
        if (visible) {
            void fetchChangeLog(userId);
            // Machine-numbered tasks start unselected; whole-task assignments are selected automatically.
            setSelectedAssignmentIds(
                machineOptions.length > 0
                    ? new Set()
                    : new Set(taskAssignments.map((a) => a.id)),
            );
            // Pre-fill skill level: if all same, use that; if mixed, leave blank so evaluator must choose
            const uniqueLevels = [...new Set(taskAssignments.map((a) => a.skillLevel).filter(Boolean))];
            const allSame = uniqueLevels.length === 1;
            setSelectedLevel(allSame ? uniqueLevels[0] : "");

            // Only treat real grades as prior evaluations — ignore assignment-time stamps on "Not Evaluated"
            const mostRecentEval = taskAssignments
                .filter((a) => isSkillEvaluated(a) && a.evaluationDate)
                .sort((a, b) => (b.evaluationDate || "").localeCompare(a.evaluationDate || ""))[0];

            setEvaluatedBy(mostRecentEval?.evaluatedByName ?? adminName);
            setEvaluationDate(
                toEvaluationCalendarDate(mostRecentEval?.evaluationDate) || toLocalCalendarDateString(),
            );
        }
    }, [visible, taskId]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleAssignment = (assignmentId: string) => {
        setSelectedAssignmentIds((prev) => {
            const next = new Set(prev);
            if (next.has(assignmentId)) next.delete(assignmentId);
            else next.add(assignmentId);
            return next;
        });
    };

    const toggleAll = () => {
        if (selectedAssignmentIds.size === taskAssignments.length) {
            setSelectedAssignmentIds(new Set());
        } else {
            setSelectedAssignmentIds(new Set(taskAssignments.map((a) => a.id)));
        }
    };

    const handleSave = () => {
        if (!selectedLevel || !evaluationDate) return;
        const targets = requiresMachineSelection
            ? taskAssignments.filter((assignment) => selectedAssignmentIds.has(assignment.id))
            : taskAssignments;
        if (requiresMachineSelection && targets.length === 0) return;

        const saveTargets =
            targets.length > 0
                ? targets
                : [{ id: '', subCategoryId: null as string | null }];

        // Store calendar date only (YYYY-MM-DD) — avoids UTC day-shift on save/display
        const evaluationDateStored = evaluationDate;

        for (const assignment of saveTargets) {
            const input: AssignSkillInput = {
                userId,
                taskId,
                companyId,
                subCategoryId: assignment.subCategoryId ?? null,
                skillLevel: selectedLevel,
                evaluatedBy: companyUser?.id ?? companyOwner?.id ?? "",
                evaluatedByName: evaluatedBy,
                evaluationDate: evaluationDateStored,
            };
            onSave(input);
        }
        onClose();
    };

    if (!visible) return null;

    const allSelected = selectedAssignmentIds.size === taskAssignments.length;

    return (
        <div className="umm-overlay" onClick={onClose}>
            <div className="umm-card" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="umm-header">
                    <h2>Grade Proficiency</h2>
                    <button className="umm-close-btn" onClick={onClose}>×</button>
                </div>

                {/* Body */}
                <div className="umm-body">
                    {/* Context labels */}
                    <p className="umm-context-inline">
                        <strong>Category:</strong> {categoryName} &middot; <strong>Classification:</strong> {classificationName}
                    </p>

                    {/* Task name — read only context */}
                    <div className="umm-field">
                        <label>TASK</label>
                        <div className="umm-readonly-value">{taskName}</div>
                    </div>

                    {/* Machine # selection — toggle pills */}
                    {requiresMachineSelection && (
                        <div className="umm-field">
                            <label>
                                MACHINE # <span aria-hidden="true">*</span>
                            </label>
                            <p className="umm-machine-select-prompt">
                                Select which Machine # to grade
                            </p>
                            <div
                                className="umm-machine-pills"
                                role="group"
                                aria-label="Select which Machine # to grade"
                            >
                                <button
                                    type="button"
                                    className={`umm-machine-pill ${allSelected ? "selected" : ""}`}
                                    onClick={toggleAll}
                                >
                                    All
                                </button>
                                {machineOptions.map((opt) => {
                                    const levelColor = getSkillLevelColor(opt.level, skillLevels);
                                    const isSelected = selectedAssignmentIds.has(opt.id);
                                    return (
                                        <button
                                            key={opt.id}
                                            type="button"
                                            className={`umm-machine-pill ${isSelected ? "selected" : ""}`}
                                            style={isSelected
                                                ? undefined
                                                : { borderLeftWidth: 4, borderLeftColor: levelColor.dot, borderLeftStyle: "solid" as const }
                                            }
                                            onClick={() => toggleAssignment(opt.id)}
                                            aria-pressed={isSelected}
                                        >
                                            <span>{opt.name}</span>
                                            <span className="umm-machine-pill-level" style={{ color: isSelected ? "rgba(255,255,255,0.8)" : levelColor.text }}>
                                                {opt.level}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    {!requiresMachineSelection && (
                        <p className="umm-description">
                            This task has no machine numbers. The grade will apply to the whole task.
                        </p>
                    )}

                    {/* Skill Level */}
                    <div className="umm-field">
                        <label>SKILL LEVEL *</label>
                        <div className="umm-skill-level-group">
                            {levelOptions.map((level) => {
                                const colorSet = getSkillLevelColor(level, skillLevels);
                                const isSelected = selectedLevel === level;
                                return (
                                    <button
                                        key={level}
                                        type="button"
                                        className={`umm-skill-level-btn ${isSelected ? "selected" : ""}`}
                                        style={isSelected
                                            ? { background: colorSet.dot, borderColor: colorSet.dot, color: "#fff" }
                                            : { background: colorSet.bg, borderColor: colorSet.bg, color: colorSet.text }
                                        }
                                        onClick={() => setSelectedLevel(level)}
                                    >
                                        {level}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Evaluated by */}
                    <div className="umm-field">
                        <label>EVALUATED BY</label>
                        <input
                            type="text"
                            value={evaluatedBy}
                            onChange={(e) => setEvaluatedBy(e.target.value)}
                        />
                    </div>

                    <div className="umm-field">
                        <label htmlFor="umm-evaluation-date">EVALUATION DATE</label>
                        <DateField
                            inputId="umm-evaluation-date"
                            ariaLabel="Evaluation date"
                            value={evaluationDate}
                            onChange={setEvaluationDate}
                        />
                    </div>

                    {evaluationHistory.length > 0 && (
                        <div className="umm-field umm-eval-history">
                            <label>EVALUATION HISTORY</label>
                            <ul className="umm-eval-history-list">
                                {evaluationHistory.map((entry, index) => (
                                    <li
                                        key={`${entry.evaluationDate}-${entry.skillLevel}-${entry.subCategoryId || entry.machineName || ''}-${index}`}
                                        className="umm-eval-history-item"
                                    >
                                        <span className="umm-eval-history-date">
                                            {formatEvaluationDateDisplay(entry.evaluationDate)}
                                        </span>
                                        {entry.machineName ? (
                                            <span className="umm-eval-history-machine">{entry.machineName}</span>
                                        ) : (
                                            <span className="umm-eval-history-machine umm-eval-history-machine-empty">
                                                —
                                            </span>
                                        )}
                                        <span className="umm-eval-history-level">{entry.skillLevel}</span>
                                        <span className="umm-eval-history-by">
                                            {entry.evaluatedByName || "—"}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="umm-footer">
                    <button className="umm-btn-cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="umm-btn-primary"
                        disabled={
                            !selectedLevel ||
                            !evaluationDate ||
                            (requiresMachineSelection && selectedAssignmentIds.size === 0)
                        }
                        onClick={handleSave}
                    >
                        Save
                    </button>
                </div>
            </div>
        </div>
    );
};
